import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { PaintedTextField } from '@/components/admin/variables/painted-text-field';
import { VariableMenu } from '@/components/admin/variables/variable-menu';
import type { PaletteContent } from '@/lib/variable-palette';
import { firstPaletteToken, paletteFullSentence } from '@/lib/variable-palette';
import type { VariableZone } from '@/lib/variable-pill';
import { EXPERIENCE_OVERLAY_MAX } from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * La surcouche de §10.2 : le texte ajouté à la suite de la direction du persona,
 * pour cette expérience seulement (maquette `OngletPersona` ; la pastille de
 * §8.6 depuis le grill Variables du 2026-09-16, D7, issue #88).
 *
 * §10.2 makes it the ONE place the persona meets the variables of §8.6 - « le
 * seul endroit du persona où les variables @ et le bouton {} reviennent » - and
 * caps it at 2000 characters (H23). Both are here: the `{ }` button and the `@`
 * typed in the field open the same menu, and the counter under the field counts
 * against the cap the schema holds.
 *
 * ## Le champ peint, et la même palette que le Contexte (D7)
 *
 * « Tu t'adresses à @prénom » is a sentence about the character, not about the
 * situation, so the overlay keeps its variables now that the Contexte of §9.5
 * exists - and it draws them as the same pill the four other zones draw, in a
 * painted field (`variables/painted-text-field.tsx`). It was a `<textarea>`
 * showing the stored token while the pill was drawn in the panel below; a pill
 * inside the zone needs a painted `contentEditable`, which the page editor of
 * #58 brought and #88 generalised.
 *
 * The menu follows the overlay's PLACE (D3): served while the protocol plays,
 * it offers the fields and choices of the pages before the protocol page, the
 * objectives, and the criteria. A choice inserted here is `{{choix:<pageId>}}`
 * - the menu carries the token itself, where the old list rebuilt a
 * `{{champ:…}}` from a block id and wrote a field for every choice.
 *
 * ## Inactive without a persona, and the field says why
 *
 * §10.2: an overlay prolongs a direction, and « sans persona choisi » there is
 * none. The field is then read-only rather than `disabled`, the régime this
 * repo holds everywhere: a disabled control leaves the tab order and its text
 * stops being selectable, and the state made for READING would be the only one
 * where the text cannot be copied out.
 */

export interface ExperiencePersonaOverlayProps {
  value: string;
  /** Inactive without a persona (§10.2), and while a version is being read. */
  readOnly: boolean;
  /** What each token of the overlay IS, and where « Ouvrir » lands (D5, D6). */
  zone: VariableZone;
  /** The `@` menu at the overlay's place, for the letters typed after the `@` (D3). */
  palette: (query: string) => PaletteContent;
  onChange: (next: string) => void;
}

export function ExperiencePersonaOverlay({
  value,
  readOnly,
  zone,
  palette,
  onChange,
}: ExperiencePersonaOverlayProps) {
  const labelId = useId();

  return (
    <div className="space-y-1.5">
      <p id={labelId} className="block font-semibold text-bleu-nuit text-small">
        Surcouche pour cette expérience
      </p>
      <p className="text-gray-600 text-caption leading-4">
        Ajoutée à la suite de la direction du persona, pour cette expérience seulement.
      </p>
      <PaintedTextField
        value={value}
        readOnly={readOnly}
        placeholder={readOnly ? 'Choisis d’abord un persona' : ''}
        ariaLabelledBy={labelId}
        testId={EXPERIENCE_IDS.overlay}
        insertTestId={EXPERIENCE_IDS.overlayVariable}
        minHeightClass="min-h-32"
        zone={zone}
        renderMenu={(query, insert, fits) => (
          <VariableMenu
            content={palette(query ?? '')}
            query={query}
            onInsert={insert}
            fits={fits}
            full={paletteFullSentence(EXPERIENCE_OVERLAY_MAX)}
            className="mt-2"
          />
        )}
        firstToken={(query) => firstPaletteToken(palette(query))}
        // The counter is the bound, refused and never cut (#160).
        counter={{ max: EXPERIENCE_OVERLAY_MAX }}
        hint={readOnly ? 'Choisis d’abord un persona' : undefined}
        onChange={onChange}
      />
    </div>
  );
}
